import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logout from '../Logout';
import { LEARN_HUB } from '../../config/appLabels';
import { NavRouteIcon } from '../Items/navIcons';
import { GAMIFICATION_MENU } from '../../config/gamificationNav';
import { LEARNING_MENU } from '../../config/learningNav';
import '../Items/styles/Navbar.css';
import { useUserPlan } from '../../hooks/useUserPlan';
import { usePlanGate } from '../../hooks/usePlanGate';
import PlanTierLabel from '../billing/PlanTierLabel';
import { FEATURE } from '../../config/planFeatures';

export default function AppMobileDrawer({
  open, onClose, items, adminItems = [], userProfile, onNavigate, pendingTeamRequests = 0, unreadNotifications = 0,
}) {
  const navigate = useNavigate();
  const { canAccessTeams, canUseFeature } = useUserPlan();
  const { navigateIfAllowed } = usePlanGate();

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const go = (path) => {
    if (path) {
      sessionStorage.setItem('lastVisitedPage', path);
      navigateIfAllowed(path);
      onNavigate?.(path);
    }
    onClose();
  };

  const linkLabel = (item) => (typeof item.label === 'string' ? item.label : item.navLabel || 'Link');

  return (
    <>
      <button type="button" className="app-drawer-backdrop" aria-label="Close menu" onClick={onClose} />
      <aside className="app-drawer" role="dialog" aria-modal="true" aria-label="App navigation">
        <div className="app-drawer-head">
          <strong>{userProfile?.fullName || 'Menu'}</strong>
          <button type="button" className="app-drawer-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        {userProfile?.email && (
          <p className="app-drawer-email">{userProfile.email}</p>
        )}
        <nav className="app-drawer-nav">
          {items.filter((i) => i.path).map((item) => (
            <button
              key={item.index}
              type="button"
              className={[
                'app-drawer-link',
                item.active ? 'app-drawer-link--active' : '',
                item.learningCore && !item.active ? 'app-drawer-link-learning' : '',
              ].filter(Boolean).join(' ')}
              aria-current={item.active ? 'page' : undefined}
              onClick={() => go(item.path)}
            >
              <NavRouteIcon path={item.path} className="app-drawer-link-icon" />
              {linkLabel(item)}
              {item.path === '/teams' && !canAccessTeams && <PlanTierLabel tier="Team" />}
              {item.path === '/teams' && pendingTeamRequests > 0 && (
                <span className="app-nav-badge">{pendingTeamRequests > 9 ? '9+' : pendingTeamRequests}</span>
              )}
            </button>
          ))}
          <p className="app-drawer-section-label">Personalized learning</p>
          {LEARNING_MENU.map((item) => (
            <button
              key={item.path}
              type="button"
              className="app-drawer-link app-drawer-link-learning"
              onClick={() => go(item.path)}
            >
              <NavRouteIcon path={item.path} className="app-drawer-link-icon" />
              {item.label}
            </button>
          ))}
          <p className="app-drawer-section-label">Progress &amp; rank</p>
          {GAMIFICATION_MENU.map((item) => (
            <button
              key={item.path}
              type="button"
              className="app-drawer-link app-drawer-link-learning"
              onClick={() => go(item.path)}
            >
              <NavRouteIcon path={item.path} className="app-drawer-link-icon" />
              {item.label}
              {item.path === '/leaderboard'
                && !canUseFeature(FEATURE.LEADERBOARD_RANK) && <PlanTierLabel tier="Pro" />}
            </button>
          ))}
          <p className="app-drawer-section-label">Account</p>
          <Link to="/notifications" className="app-drawer-link" onClick={onClose}>
            Notifications
            {unreadNotifications > 0 && (
              <span className="app-nav-badge">{unreadNotifications > 9 ? '9+' : unreadNotifications}</span>
            )}
          </Link>
          <Link to="/settings?tab=billing" className="app-drawer-link" onClick={onClose}>
            Plan & billing
          </Link>
          <Link to={LEARN_HUB.path} className="app-drawer-link app-drawer-link-learning" onClick={onClose}>
            {LEARN_HUB.name} — {LEARN_HUB.summary}
          </Link>
          <Link to="/settings/learning" className="app-drawer-link" onClick={onClose}>
            Learning interests
          </Link>
          <Link to="/settings" className="app-drawer-link" onClick={onClose}>
            Settings
          </Link>
          {adminItems.length > 0 && (
            <>
              <p className="app-drawer-section-label">Admin settings</p>
              {adminItems.map((item) => (
                <button
                  key={item.path}
                  type="button"
                  className="app-drawer-link"
                  onClick={() => go(item.path)}
                >
                  {item.label}
                </button>
              ))}
            </>
          )}
        </nav>
        <div className="app-drawer-foot">
          <Logout />
        </div>
      </aside>
    </>
  );
}
